"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
} from "@devhop/ui";
import {
  CrownIcon,
  ShieldCheckIcon,
  UserCheckIcon,
  UserIcon,
} from "lucide-react";
import { useState } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useUpdateKycTierMutation } from "../api/queries";

const KYC_TIER_OPTIONS = [
  { value: "unverified", label: "ບໍ່ຢືນຢັນ" },
  { value: "basic", label: "ພື້ນຖານ" },
  { value: "verified", label: "ຢືນຢັນ" },
  { value: "premium", label: "ພຣີມຽມ" },
] as const;

const KYC_TIER_STYLE: Record<
  string,
  {
    icon: typeof UserIcon;
    color: string;
    variant: "default" | "secondary" | "outline" | "destructive";
    label: string;
  }
> = {
  unverified: {
    icon: UserIcon,
    color: "text-destructive-foreground",
    variant: "destructive",
    label: "ບໍ່ຢືນຢັນ",
  },
  basic: {
    icon: UserCheckIcon,
    color: "text-secondary-foreground",
    variant: "secondary",
    label: "ພື້ນຖານ",
  },
  verified: {
    icon: ShieldCheckIcon,
    color: "text-primary-foreground",
    variant: "default",
    label: "ຢືນຢັນ",
  },
  premium: {
    icon: CrownIcon,
    color: "text-primary",
    variant: "outline",
    label: "ພຣີມຽມ",
  },
};

type WalletAccountKycTierCellProps = {
  walletId: string;
  kycTier: string;
};

export function WalletAccountKycTierCell({
  walletId,
  kycTier,
}: WalletAccountKycTierCellProps) {
  const { has } = usePermissions();
  const canEdit = has("compliance:update");
  const mutation = useUpdateKycTierMutation();
  const [pendingTier, setPendingTier] = useState<string | null>(null);

  const style = KYC_TIER_STYLE[kycTier] ?? {
    icon: UserIcon,
    color: "text-muted-foreground",
    variant: "outline" as const,
    label: kycTier,
  };
  const TierIcon = style.icon;

  if (!canEdit) {
    return (
      <Badge variant={style.variant} className="gap-1">
        <TierIcon className={`size-3 ${style.color}`} />
        {style.label}
      </Badge>
    );
  }

  return (
    <>
        <SimpleSelect
          value={kycTier}
          disabled={mutation.isPending}
          onValueChange={(next) => {
            if (next === kycTier) return;
            setPendingTier(next);
          }}
          className="h-8 min-w-[130px]"
          options={KYC_TIER_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />

        <AlertDialog
          open={pendingTier != null}
          onOpenChange={(open) => {
            if (!open) setPendingTier(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ປ່ຽນ KYC Tier?</AlertDialogTitle>
              <AlertDialogDescription>
                ປ່ຽນຈາກ{" "}
                <strong>
                  {KYC_TIER_STYLE[kycTier]?.label ?? kycTier}
                </strong>{" "}
                ເປັນ{" "}
                <strong>
                  {pendingTier
                    ? (KYC_TIER_STYLE[pendingTier]?.label ?? pendingTier)
                    : "—"}
                </strong>
                . ລູກຄ້າຈະໄດ້ຮັບອີເມວແຈ້ງເຕືອນ (ຖ້າເປັນບັນຊີລູກຄ້າ)
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending}>
                ຍົກເລີກ
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={mutation.isPending || !pendingTier}
                onClick={() => {
                  if (!pendingTier) return;
                  mutation.mutate(
                    { walletId, kycTier: pendingTier },
                    { onSettled: () => setPendingTier(null) },
                  );
                }}
              >
                {mutation.isPending ? "ກຳລັງບັນທຶກ..." : "ຢືນຢັນ"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </>
  );
}
